import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { gradientBorderVariants } from './variants';

export interface GradientBorderProps extends HTMLAttributes<HTMLDivElement> {
  /** Ring thickness in pixels. Omit to use the default (2). */
  borderWidth?: number;
  /** Content rendered inside the ring; direct children receive stacking above the pseudo-element. */
  children: ReactNode;
}

/**
 * Renders a gradient “border” using a masked `::before` ring (`src/index.css` `.gradient-border`).
 * Direct children are given `relative z-10` so they paint above the ring.
 *
 * @example
 * ```tsx
 * <GradientBorder borderWidth={3} className="inline-flex rounded-pill">
 *   <button type="button">Claim</button>
 * </GradientBorder>
 * ```
 */
const GradientBorder = forwardRef<HTMLDivElement, GradientBorderProps>(
  ({ borderWidth, className, children, style, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(gradientBorderVariants(), className)}
      style={{
        ...(borderWidth !== undefined ? { '--gradient-border-width': `${borderWidth}px` } : {}),
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  ),
);
GradientBorder.displayName = 'GradientBorder';

export { GradientBorder };
